import axios from "axios";
import { baseUrl } from "../baseUrl/BaseUrl";

// Verrou pour éviter les appels refresh en parallèle
let isRefreshing = false;
// File d'attente des requêtes en attente du nouveau token
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

// Création de l'instance Axios
const axiosInstance = (accessToken) => {
    const authorization = accessToken ? `Bearer ${accessToken}` : "";
    const instance = axios.create({
        baseURL: baseUrl,
        withCredentials: false,
        headers: {
            Authorization: authorization,
        },
    });

    // ─── Intercepteur de réponse : gestion des 401 ───────────────────────────
    instance.interceptors.response.use(
        // Succès → passer tel quel
        (response) => response,

        async (error) => {
            const originalRequest = error.config;

            // Si ce n'est pas un 401, ou si c'est déjà une tentative de retry,
            // ou si c'est l'endpoint de refresh lui-même → on rejette directement
            if (
                !error.response ||
                error.response.status !== 401 ||
                originalRequest._retry ||
                originalRequest.url?.includes('api/auth/refresh')
            ) {
                return Promise.reject(error);
            }

            // Marquer cette requête comme déjà retentée
            originalRequest._retry = true;

            const refreshToken = sessionStorage.getItem('refreshTokenAdmin');

            // Pas de refresh token disponible → déconnexion
            if (!refreshToken) {
                // Import lazy pour éviter la dépendance circulaire
                const { default: store } = await import("../store/store");
                const { logOut } = await import("../features/authSliceUser");
                store.dispatch(logOut());
                window.location.href = '/login';
                return Promise.reject(error);
            }

            // Si un refresh est déjà en cours, mettre en file d'attente
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                }).then(token => {
                    originalRequest.headers['Authorization'] = `Bearer ${token}`;
                    return axios({ ...originalRequest, baseURL: baseUrl });
                }).catch(err => {
                    return Promise.reject(err);
                });
            }

            isRefreshing = true;

            try {
                // Import lazy pour éviter la dépendance circulaire
                const { default: store } = await import("../store/store");
                const { refreshUserToken, logOut } = await import("../features/authSliceUser");

                // Appel du thunk de refresh (gère le compteur et la limite de 10)
                const newToken = await store.dispatch(refreshUserToken(refreshToken));

                if (!newToken) {
                    // Refresh refusé (limite atteinte ou échec) → le modal est déjà ouvert
                    processQueue(error, null);
                    return Promise.reject(error);
                }

                // Refresh réussi : rejouer les requêtes en file d'attente + la requête originale
                processQueue(null, newToken);
                originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                return axios({ ...originalRequest, baseURL: baseUrl });
            } catch (refreshError) {
                processQueue(refreshError, null);
                const { default: store } = await import("../store/store");
                const { logOut } = await import("../features/authSliceUser");
                store.dispatch(logOut());
                window.location.href = '/login';
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }
    );

    return instance;
};

export default axiosInstance;